import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { API_CONFIG, fetchWithTimeout, getAuthHeaders, processApiResponse } from '../config/api';

import '../styles/AdminDashboard.css';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { logout, getToken, getUserInfo } = useAuth();

  // Estados para gerenciar os dados
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userIndications, setUserIndications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showIndicationsModal, setShowIndicationsModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({
    nome: '',
    foto: '',
    email: '',
    telefone: '',
    ra: '',
    password: ''
  });
  const [createUserLoading, setCreateUserLoading] = useState(false);

  // Estados para mensagens de notificação
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success' // 'success' ou 'error'
  });

  // Estados para edição de indicação
  const [showEditIndicationModal, setShowEditIndicationModal] = useState(false);
  const [editingIndication, setEditingIndication] = useState(null);
  const [editIndicationForm, setEditIndicationForm] = useState({
    name: '',
    status: ''
  });
  const [editIndicationLoading, setEditIndicationLoading] = useState(false);
  const [deleteIndicationLoading, setDeleteIndicationLoading] = useState(false);
  const [loadingIndications, setLoadingIndications] = useState(false);
  const [openActionsPopup, setOpenActionsPopup] = useState(null);

  // Estados para edição de usuário
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({
    nome: '',
    foto: '',
    email: '',
    telefone: '',
    ra: '',
    password: '',
    admin: false,
    active: true
  });
  const [editUserLoading, setEditUserLoading] = useState(false);

  // Estado para visualização mobile de usuário
  const [showUserViewModal, setShowUserViewModal] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);

  const userInfoItems = getUserInfo();

  // Função para mostrar notificações
  const showNotification = (message, type = 'success') => {
    setNotification({
      show: true,
      message,
      type
    });

    // Auto-hide após 5 segundos
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 5000);
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const headers = getAuthHeaders(token);

      const response = await fetchWithTimeout(API_CONFIG.GET_ALL_USERS_URL, {
        method: 'GET',
        headers
      });

      const data = await processApiResponse(response);

      if (data.allUsers && Array.isArray(data.allUsers)) {
        setUsers(data.allUsers);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      const errorMessage = error.message || error.data?.message || error.data?.error || 'Erro ao carregar usuários. Tente novamente.';
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers()
  }, []);

  useEffect(() => {
    if (openActionsPopup === null) return;
    const handleClickOutside = () => setOpenActionsPopup(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openActionsPopup]);

  const fetchIndications = async (userId) => {
    try {
      const token = getToken();
      const headers = getAuthHeaders(token);

      const requestBody = {
        id_users: userId
      };

      const response = await fetchWithTimeout(API_CONFIG.GET_INDICATIONS_URL, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      });

      const data = await processApiResponse(response);

      if (data.indications && Array.isArray(data.indications)) {
        setUserIndications(data.indications);
      } else {
        setUserIndications([]);
      }
    } catch (error) {
      console.error('Erro ao carregar indicações:', error);
      const errorMessage = error.message || error.data?.message || error.data?.error || 'Erro ao carregar indicações. Tente novamente.';
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const copyEmailToClipboard = (email) => {
    if (!email) return;
    navigator.clipboard.writeText(email).then(() => {
      showNotification('E-mail copiado para a área de transferência!');
    }).catch(() => {
      showNotification('Não foi possível copiar o e-mail.', 'error');
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/login-admin');
  };

  const handleUserClick = async (user) => {
    if (loadingIndications) return;

    setLoadingIndications(true);
    setSelectedUser(user);
    setShowIndicationsModal(true);

    try {
      await fetchIndications(user.id);
    } finally {
      setLoadingIndications(false);
    }
  };

  const handleCloseModal = () => {
    setShowIndicationsModal(false);
    setSelectedUser(null);
    setUserIndications([]);
  };

  const handleOpenCreateUserModal = () => {
    setShowCreateUserModal(true);
  };

  const handleCloseCreateUserModal = () => {
    setShowCreateUserModal(false);
    setCreateUserForm({
      nome: '',
      foto: '',
      email: '',
      telefone: '',
      ra: '',
      password: '',
      admin: false
    });
  };

  // Funções para edição de indicação
  const handleEditIndication = (indication) => {
    setEditingIndication(indication);
    setEditIndicationForm({
      name: indication.name,
      status: indication.status
    });
    setShowEditIndicationModal(true);
  };

  const handleCloseEditIndicationModal = () => {
    setShowEditIndicationModal(false);
    setEditingIndication(null);
    setEditIndicationForm({
      name: '',
      status: ''
    });
  };

  const handleEditIndicationInputChange = (e) => {
    const { name, value } = e.target;
    setEditIndicationForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditIndicationSubmit = async (e) => {
    e.preventDefault();

    if (editIndicationLoading) return;

    setEditIndicationLoading(true);

    try {
      const token = getToken();
      const headers = getAuthHeaders(token);

      const indicationData = {
        indication: {
          id: editingIndication.id,
          name: editIndicationForm.name,
          status: editIndicationForm.status
        }
      };

      const response = await fetchWithTimeout(API_CONFIG.UPDATE_INDICATIONS_URL, {
        method: 'PUT',
        headers,
        body: JSON.stringify(indicationData)
      });

      const data = await processApiResponse(response);

      showNotification(data.message || 'Indicação editada com sucesso!', 'success');
      handleCloseEditIndicationModal();
      // Recarregar indicações do usuário
      if (selectedUser) {
        fetchIndications(selectedUser.id);
      }
    } catch (error) {
      console.error('Erro ao editar indicação:', error);
      const errorMessage = error.message || error.data?.message || error.data?.error || 'Erro ao editar indicação. Tente novamente.';
      showNotification(errorMessage, 'error');
    } finally {
      setEditIndicationLoading(false);
    }
  };

  const handleDeleteIndication = async (indication) => {
    if (deleteIndicationLoading) return;

    if (!window.confirm(`Tem certeza que deseja excluir a indicação de ${indication.name}?`)) {
      return;
    }

    setDeleteIndicationLoading(true);

    try {
      const token = getToken();
      const headers = getAuthHeaders(token);

      const response = await fetchWithTimeout(`${API_CONFIG.DELETE_INDICATIONS_URL}/${indication.id}`, {
        method: 'DELETE',
        headers
      });

      const data = await processApiResponse(response);

      showNotification(data.message || 'Indicação excluída com sucesso!', 'success');
      // Recarregar indicações do usuário
      if (selectedUser) {
        fetchIndications(selectedUser.id);
      }
    } catch (error) {
      console.error('Erro ao excluir indicação:', error);
      const errorMessage = error.message || error.data?.message || error.data?.error || 'Erro ao excluir indicação. Tente novamente.';
      showNotification(errorMessage, 'error');
    } finally {
      setDeleteIndicationLoading(false);
    }
  };

  // Funções para edição de usuário
  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditUserForm({
      nome: user.nome || '',
      foto: user.foto || '',
      email: user.email || '',
      telefone: user.telefone || '',
      ra: user.ra || '',
      password: '', // Sempre vazio para edição
      admin: user.admin,
      active: user.active === true,
      indicationLimit: user.indicationLimit || 0
    });
    setShowEditUserModal(true);
  };

  const handleUserRowClick = (user) => {
    setViewingUser(user);
    setShowUserViewModal(true);
  };

  const handleCloseUserViewModal = () => {
    setShowUserViewModal(false);
    setViewingUser(null);
  };

  const handleCloseEditUserModal = () => {
    setShowEditUserModal(false);
    setEditingUser(null);
    setEditUserForm({
      nome: '',
      foto: '',
      email: '',
      telefone: '',
      ra: '',
      password: '',
      admin: false,
      active: true,
      indicationLimit: 0
    });
  };

  const handleEditUserInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditUserForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };


  const handleEditUserSubmit = async (e) => {
    e.preventDefault();

    if (editUserLoading) return;

    setEditUserLoading(true);

    try {
      const token = getToken();
      const headers = getAuthHeaders(token);

      const userData = {
        user: {
          id: editingUser.id,
          nome: editUserForm.nome,
          foto: editUserForm.foto,
          email: editUserForm.email,
          telefone: editUserForm.telefone.replace(/\D/g, ''),
          ra: editUserForm.ra,
          password: editUserForm.password || undefined, // Só envia se preenchido
          admin: true,
          active: editUserForm.active,
          indicationLimit: parseInt(editUserForm.indicationLimit)
        }
      };

      const response = await fetchWithTimeout(API_CONFIG.UPDATE_USER_URL, {
        method: 'PUT',
        headers,
        body: JSON.stringify(userData)
      });

      const data = await processApiResponse(response);

      showNotification(data.message || 'Usuário editado com sucesso!', 'success');
      handleCloseEditUserModal();
      fetchUsers(); // Recarregar lista de usuários
    } catch (error) {
      console.error('Erro ao editar usuário:', error);
      const errorMessage = error.message || error.data?.message || error.data?.error || 'Erro ao editar usuário. Tente novamente.';
      showNotification(errorMessage, 'error');
    } finally {
      setEditUserLoading(false);
    }
  };

  const handleCreateUserInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCreateUserForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const formatPhoneInput = (value) => {
    const phoneClean = value.replace(/\D/g, '');
    if (phoneClean.length <= 11) {
      if (phoneClean.length <= 2) {
        return phoneClean;
      } else if (phoneClean.length <= 7) {
        return `(${phoneClean.slice(0, 2)}) ${phoneClean.slice(2)}`;
      } else {
        return `(${phoneClean.slice(0, 2)}) ${phoneClean.slice(2, 7)}-${phoneClean.slice(7, 11)}`;
      }
    }
    return value;
  };

  const handleCreateUserSubmit = async (e) => {
    e.preventDefault();

    if (createUserLoading) return;

    setCreateUserLoading(true);

    try {
      const token = getToken();
      const headers = getAuthHeaders(token);

      const requestBody = {
        user: {
          ...createUserForm,
          telefone: createUserForm.telefone.replace(/\D/g, '') // Remove formatação do telefone
        }
      };

      const response = await fetchWithTimeout(API_CONFIG.CREATE_USER_URL, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      });

      const data = await processApiResponse(response);

      showNotification(data.message || 'Usuário criado com sucesso!', 'success');
      handleCloseCreateUserModal();
      fetchUsers(); // Recarregar lista de usuários
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      const errorMessage = error.message || error.data?.message || error.data?.error || 'Erro ao criar usuário. Tente novamente.';
      showNotification(errorMessage, 'error');
    } finally {
      setCreateUserLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Data não informada';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const formatPhone = (phone) => {
    if (!phone) return 'Telefone não informado';
    const phoneClean = phone.replace(/\D/g, '');
    if (phoneClean.length === 11) {
      return `(${phoneClean.slice(0, 2)}) ${phoneClean.slice(2, 7)}-${phoneClean.slice(7)}`;
    } else if (phoneClean.length === 10) {
      return `(${phoneClean.slice(0, 2)}) ${phoneClean.slice(2, 6)}-${phoneClean.slice(6)}`;
    }
    return phone;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Ativo':
        return 'status-active';
      case 'Inativo':
        return 'status-inactive';

      case 'admin':
        return 'status-admin';
      case 'no-admin':
        return 'status-no-admin';

      case 'EXPIRED':
        return 'status-expired';
      case 'SENT':
        return 'status-sent';
      case 'IN_SERVICE':
        return 'status-service';
      case 'PURCHASE_CONFIRMED':
        return 'status-purchase-confirmed';
      case 'DISCOUNT_PROCESSING':
        return 'status-processing';
      case 'DISCOUNT_RELEASED':
        return 'status-active';
      default:
        return 'status-pending';
    }
  };

  const filteredUsers = users.filter(user => {
    const nome = user.nome || "";
    const ra = user.ra || "";
    const email = user.email || "";

    return (
      nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ra.includes(searchTerm) ||
      email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }).sort((a, b) => {
    const aAdmin = a.admin ? true : false;
    const bAdmin = b.admin ? true : false;

    return bAdmin - aAdmin;
  });

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Carregando dados administrativos...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container admin">
      {/* Notificação */}
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          <div className="notification-content">
            <span className="notification-message">{notification.message}</span>
            <button
              className="notification-close"
              onClick={() => setNotification(prev => ({ ...prev, show: false }))}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="dashboard-header">
        <div className="header-left">
          <div className="logo-container">
            <img alt='Amigo Indica Amigo' src='/images/admin/logo-aia-vermelho.svg' className='logo' width={49} height={39} />
          </div>
        </div>
        <div className="header-center">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Pesquisar usuários..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <img alt='' src='/images/admin/svg/search.svg' fill="currentColor" />
          </div>
        </div>
        <div className="header-right">
          <button className="create-user-btn top-nav-btn" onClick={handleOpenCreateUserModal}>
            <img src='/images/dashboard/svg/new_window.svg' alt='Criar Usuário' className="top-nav-btn-icon-desktop" />
            <img src='/images/dashboard/svg/new_window_gray.svg' alt='Criar Usuário' className="top-nav-btn-icon-mobile" />
            <span className="top-nav-btn-text">Novo Usuário</span>
          </button>
          <button className="logout-btn top-nav-btn" onClick={handleLogout}>
            <img src='/images/dashboard/svg/move_item.svg' alt='Criar Usuário' className="top-nav-btn-icon-desktop" />
            <span className="top-nav-btn-text">Sair</span>
          </button>
        </div>
      </header>

      {/* User Profile Section */}
      <section className="profile-section">
        <div className="profile-content">
          <div className="profile-left">
              {userInfoItems?.avatar ? (
                <img
                  src={userInfoItems.avatar}
                  alt={userInfoItems.nome || "Usuário"}
                  className="avatar-img"
                />
              ) : (
                <img alt='' src='/images/admin/adminstrador.png' className="avatar-img" />
              )}
            {/* <div className="user-info"> */}
              <div className="user-name-wrapper">
                <h2 className="user-name">{userInfoItems?.nome || "Usuário"}</h2>
                {userInfoItems?.isAdmin && (
                  <div className="admin-badge">Conta Administrativa</div>
                )}
              </div>
              <div className="user-details">
                <div className="detail-item ra">
                  <img alt='' src='/images/admin/svg/home_storage.svg' fill="currentColor" />
                  <h3 className="detail-item-text">
                    Login:
                    <span> {userInfoItems?.ra || "0000000"}</span>
                  </h3>
                </div>
                <div className="detail-item email" onClick={() => copyEmailToClipboard(userInfoItems?.email)} style={{ cursor: 'pointer' }}>
                  <img alt='' src='/images/admin/svg/home_storage.svg' fill="currentColor" />
                  <h3 className="detail-item-text">
                    E-mail:
                    <span> {userInfoItems?.email || "email@exemplo.com"}</span>
                  </h3>
                </div>
              </div>
            {/* </div> */}
          </div>
          <div className="profile-right">
            <div className="search-bar mobile-search">
              <input
                type="text"
                placeholder="Pesquisar usuários..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <img alt='' src='/images/admin/svg/search_mobile.svg'/>
            </div>
            <div className="metric-card">
              <div className="metric-label">Total de Usuários</div>
              <div className="metric-value">{users.length}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Usuários Ativos</div>
              <div className="metric-value">{users.filter(u => u.active === true).length}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="dashboard-main admin">
        <div className="table-container">
          <h3 className='table-title'>Lista de Usuários</h3>
          <table className="users-table">
            <thead>
              <tr>
                <th className='th-login'>Login</th>
                <th className='th-name'>Nome</th>
                <th className='th-email'>E-mail</th>
                <th className='th-status'>Status</th>
                <th className='th-type'>Tipo de acesso</th>
                <th className='th-date'>Data de Cadastro</th>
                <th className='th-actions'>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="loading-cell">
                    <div className="loading-spinner"></div>
                    Carregando usuários...
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="8" className="empty-cell">
                    Nenhum usuário encontrado
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="user-row" onClick={() => handleUserRowClick(user)}>
                    <td title={user.ra}>{user.ra}</td>
                    <td title={user.nome}>{user.nome}</td>
                    <td title={user.email}>{user.email}</td>
                    <td>
                      <span className={`status-badge ${getStatusColor(user.active === true ? "Ativo" : "Inativo")}`}>
                        {user.active === true ? "Ativo" : "Inativo"}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusColor(user.admin === true ? "admin" : "no-admin")}`}>
                        {user.admin === true ? "Admin" : "Aluno"}
                      </span>
                    </td>
                    <td className="user-date">{formatDate(user.created_at)}</td>
                    <td className="action-buttons">
                      {user.admin === false ?
                        <button
                          className="action-btn view-btn"
                          onClick={(e) => { e.stopPropagation(); handleUserClick(user); }}
                          disabled={loadingIndications}
                          title="Ver Indicações"
                        >
                          <svg width="19" height="17" viewBox="0 0 19 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9.16667 13.5C7.06944 13.5 5.20139 12.9201 3.5625 11.7604C1.92361 10.6007 0.736111 9.09722 0 7.25C0.736111 5.40278 1.92361 3.89931 3.5625 2.73958C5.20139 1.57986 7.06944 1 9.16667 1C11.2639 1 13.1319 1.57986 14.7708 2.73958C16.4097 3.89931 17.5972 5.40278 18.3333 7.25C17.5972 9.09722 16.4097 10.6007 14.7708 11.7604C13.1319 12.9201 11.2639 13.5 9.16667 13.5ZM9.16667 11C10.2083 11 11.0938 10.6354 11.8229 9.90625C12.5521 9.17708 12.9167 8.29167 12.9167 7.25C12.9167 6.20833 12.5521 5.32292 11.8229 4.59375C11.0938 3.86458 10.2083 3.5 9.16667 3.5C8.125 3.5 7.23958 3.86458 6.51042 4.59375C5.78125 5.32292 5.41667 6.20833 5.41667 7.25C5.41667 8.29167 5.78125 9.17708 6.51042 9.90625C7.23958 10.6354 8.125 11 9.16667 11Z" fill="#949494" />
                          </svg>
                        </button>
                        : ""}
                      <button
                        className="action-btn edit-btn"
                        onClick={(e) => { e.stopPropagation(); handleEditUser(user); }}
                        title="Editar Usuário"
                      >
                        <img alt='' src='/images/dashboard/svg/edit.svg' fill="currentColor" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      {/* Modal de Indicações */}
      {showIndicationsModal && selectedUser && (
        <div className="modal-overlay">
          <div className="modal-content indication-user-modal">
            <div className="modal-header">
              <button className="go-back-btn" onClick={handleCloseModal}>
                <img src="/images/dashboard/svg/arrow_back.svg" alt="" className="go-back-btn-icon" />
              </button>
              <h3 className='modal-title'>INDICAÇÕES DO ALUNO</h3>
              <button className="close-btn" onClick={handleCloseModal}>✕</button>
            </div>

            {/* User Info no Modal */}
            <div className="modal-user-info">
              <div className="profile-left">
                {selectedUser?.foto ? (
                  <img
                    src={selectedUser.foto}
                    alt={selectedUser.nome || "Usuário"}
                    className="avatar-img"
                  />
                ) : (
                  <img alt='' src='/images/dashboard/standing-6.png' className="avatar-img" />
                )}
                <div className="user-info">
                  <h2 className="user-name">{selectedUser?.nome || "Usuário"}</h2>
                  <div className="user-details">
                    <div className="detail-item">
                      <img alt='' src='/images/dashboard/svg/backpack.svg' fill="currentColor" />
                      <h3 className='detail-item-text'>
                        RA:
                        <span> {selectedUser?.ra || "0000000"}</span>
                      </h3>
                    </div>
                    <div className="detail-item">
                      <img alt='' src='/images/dashboard/svg/mail.svg' fill="currentColor" />
                      <h3 className='detail-item-text'>
                        E-mail:
                        <span> {selectedUser?.email || "email@exemplo.com"}</span>
                      </h3>
                    </div>
                  </div>
                </div>
              </div>
              <div className="profile-right">
                <div className="metric-card">
                  <div className="metric-label">Total de indicações</div>
                  <div className="metric-value">{userIndications.length}</div>
                </div>
              </div>
            </div>

            {/* Tabela de Indicações no Modal */}
            <div className="modal-table-container">
              <h3 className="table-title">Indicações do Aluno</h3>
              <table className="modal-indications-table">
                <thead>
                  <tr>
                    <th>Nome Indicado</th>
                    <th>Telefone</th>
                    <th>CPF</th>
                    <th>Status</th>
                    <th>Data</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {userIndications.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="empty-cell">
                        Nenhuma indicação encontrada
                      </td>
                    </tr>
                  ) : (
                    userIndications.map((indication) => (
                      <tr key={indication.id} className="user-row">
                        <td>{indication.name}</td>
                        <td>{formatPhone(indication.telephone)}</td>
                        <td>{indication.cpf}</td>
                        <td className="user-status">
                          <span className={`status-badge ${getStatusColor(indication.status)}`}>
                            {indication.status === 'DISCOUNT_RELEASED'
                              ? 'Desconto por indicação liberado'
                              : indication.status === 'SENT'
                                ? 'Indicação enviada'
                                : indication.status === 'IN_SERVICE'
                                  ? 'Indicação em atendimento'
                                  : indication.status === 'PURCHASE_CONFIRMED'
                                    ? 'Compra confirmada'
                                    : indication.status === 'EXPIRED'
                                      ? 'Indicação expirada'
                                      : 'Desconto por indicação em processamento'}
                          </span>
                        </td>
                        <td className="user-date">{formatDate(indication.created_at)}</td>
                        <td className="action-buttons">
                          <div className="action-buttons-group">
                            <button
                              className="edit-btn"
                              onClick={() => handleEditIndication(indication)}
                              title="Editar indicação"
                            >
                              <img alt='' src='/images/dashboard/svg/edit.svg' fill="currentColor" />
                            </button>
                            <button
                              className="delete-btn"
                              onClick={() => handleDeleteIndication(indication)}
                              disabled={deleteIndicationLoading}
                              title="Excluir indicação"
                            >
                              <img alt='' src='/images/dashboard/svg/delete_sweep.svg' fill="currentColor" />
                            </button>
                          </div>
                          <div className="more-actions-container">
                            <button
                              className="more-actions-btn"
                              onClick={(e) => { e.stopPropagation(); setOpenActionsPopup(openActionsPopup === indication.id ? null : indication.id); }}
                              title="Mais ações"
                            >
                              &#8942;
                            </button>
                            {openActionsPopup === indication.id && (
                              <div className="more-actions-popup">
                                <button
                                  className="edit-btn"
                                  onClick={() => { setOpenActionsPopup(null); handleEditIndication(indication); }}
                                  title="Editar indicação"
                                >
                                  <img alt='' src='/images/dashboard/svg/edit.svg' fill="currentColor" />
                                </button>
                                <button
                                  className="delete-btn"
                                  onClick={() => { setOpenActionsPopup(null); handleDeleteIndication(indication); }}
                                  disabled={deleteIndicationLoading}
                                  title="Excluir indicação"
                                >
                                  <img alt='' src='/images/dashboard/svg/delete_sweep.svg' fill="currentColor" />
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Criação de Usuário */}
      {showCreateUserModal && (
        <div className="modal-overlay">
          <div className="modal-content create-user-modal">
            <div className="modal-header">
              <button className="go-back-btn" onClick={handleCloseCreateUserModal}>
                <img src="/images/dashboard/svg/arrow_back.svg" alt="" className="go-back-btn-icon" />
              </button>
              <h3 className='modal-title'>CRIAR USUÁRIO ADMINISTRADOR</h3>
              <button
                className="close-btn"
                onClick={handleCloseCreateUserModal}
              >
                ✕
              </button>
            </div>

            <div className="create-user-content">
              <div className="create-user-left">
                <img alt='' src="/images/dashboard/undraw_filter_v54.svg" />
              </div>

              <div className="create-user-right">
                <form onSubmit={handleCreateUserSubmit} className="create-user-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="nome">NOME</label>
                      <input
                        type="text"
                        id="nome"
                        name="nome"
                        value={createUserForm.nome}
                        onChange={handleCreateUserInputChange}
                        placeholder="Digite o nome completo"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="ra">LOGIN</label>
                      <input
                        type="text"
                        id="ra"
                        name="ra"
                        value={createUserForm.ra}
                        onChange={handleCreateUserInputChange}
                        placeholder="Digite o Login"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="email">EMAIL</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={createUserForm.email}
                        onChange={handleCreateUserInputChange}
                        placeholder="usuario@exemplo.com"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="telefone">TELEFONE</label>
                      <div className="input-with-icon">
                        <input
                          type="text"
                          id="telefone"
                          name="telefone"
                          value={createUserForm.telefone}
                          onChange={(e) => {
                            const formatted = formatPhoneInput(e.target.value);
                            setCreateUserForm(prev => ({
                              ...prev,
                              telefone: formatted
                            }));
                          }}
                          placeholder="(11) 99999-9999"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="password">SENHA</label>
                      <input
                        type="password"
                        id="password"
                        name="password"
                        value={createUserForm.password}
                        onChange={handleCreateUserInputChange}
                        placeholder="Digite a senha"
                        required
                      />
                    </div>

                    {/* <div className="form-group">
                      <label htmlFor="foto">FOTO (URL)</label>
                      <input
                        type="text"
                        id="foto"
                        name="foto"
                        maxLength={255}
                        value={createUserForm.foto}
                        onChange={handleCreateUserInputChange}
                        placeholder="https://exemplo.com/foto.jpg"
                      />
                    </div> */}
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      className="cancel-btn"
                      onClick={handleCloseCreateUserModal}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="create-btn"
                      disabled={createUserLoading}
                    >
                      {createUserLoading ? 'Criando...' : 'Criar Usuário'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edição de Indicação */}
      {showEditIndicationModal && editingIndication && (
        <div className="modal-overlay">
          <div className="modal-content edit-indication-modal">
            <div className="modal-header">
              <button className="go-back-btn" onClick={handleCloseEditIndicationModal}>
                <img src="/images/dashboard/svg/arrow_back.svg" alt="" className="go-back-btn-icon" />
              </button>
              <h3 className='modal-title'>EDITAR INDICAÇÃO</h3>
              <button
                className="close-btn"
                onClick={handleCloseEditIndicationModal}
              >
                ✕
              </button>
            </div>

            <div className="edit-indication-content">
              <form onSubmit={handleEditIndicationSubmit} className="edit-indication-form">
                <div className="form-section">
                  <h4>Informações da Indicação</h4>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="edit-name">NOME DO INDICADO</label>
                      <input
                        type="text"
                        id="edit-name"
                        name="name"
                        value={editIndicationForm.name}
                        onChange={handleEditIndicationInputChange}
                        placeholder="Digite o nome completo"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="edit-status">STATUS</label>
                      <select
                        id="edit-status"
                        name="status"
                        value={editIndicationForm.status}
                        onChange={handleEditIndicationInputChange}
                        required
                      >
                        <option value="">Selecione o status</option>
                        <option value="SENT">Indicação enviada</option>
                        <option value="IN_SERVICE">Indicação em atendimento</option>
                        <option value="PURCHASE_CONFIRMED">Compra confirmada</option>
                        <option value="DISCOUNT_PROCESSING">Desconto por indicação em processamento</option>
                        <option value="DISCOUNT_RELEASED">Desconto por indicação liberado</option>
                        <option value="EXPIRED">Indicação expirada</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h4>Informações (Somente Leitura)</h4>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="edit-telephone">TELEFONE</label>
                      <input
                        type="text"
                        id="edit-telephone"
                        name="telephone"
                        value={formatPhone(editingIndication.telephone)}
                        disabled
                        className="disabled-input"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="edit-cpf">CPF</label>
                      <input
                        type="text"
                        id="edit-cpf"
                        name="cpf"
                        value={editingIndication.cpf}
                        disabled
                        className="disabled-input"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="edit-date">DATA DE CRIAÇÃO</label>
                      <input
                        type="text"
                        id="edit-date"
                        name="date"
                        value={formatDate(editingIndication.created_at)}
                        disabled
                        className="disabled-input"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="edit-user">USUÁRIO RESPONSÁVEL</label>
                      <input
                        type="text"
                        id="edit-user"
                        name="user"
                        value={selectedUser?.nome || 'N/A'}
                        disabled
                        className="disabled-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={handleCloseEditIndicationModal}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="save-btn"
                    disabled={editIndicationLoading}
                  >
                    {editIndicationLoading ? 'Salvando...' : 'Salvar Alterações'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edição de Usuário */}
      {showEditUserModal && editingUser && (
        <div className="modal-overlay">
          <div className="modal-content edit-user-modal">
            <div className="modal-header">
              <button className="go-back-btn" onClick={handleCloseEditUserModal}>
                <img src="/images/dashboard/svg/arrow_back.svg" alt="" className="go-back-btn-icon" />
              </button>
              <h3 className='modal-title'>{editUserForm.admin === true ? "EDITAR USUÁRIO ADMINISTRADOR" : "EDITAR USUÁRIO"}</h3>
              <button
                className="close-btn"
                onClick={handleCloseEditUserModal}
              >
                ✕
              </button>
            </div>

            <div className="edit-user-content">
              <form onSubmit={handleEditUserSubmit} className="edit-user-form">
                <div className="form-section">
                  <h4>Informações Pessoais</h4>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="edit-user-nome">NOME</label>
                      <input
                        type="text"
                        id="edit-user-nome"
                        name="nome"
                        disabled={!editUserForm.admin}
                        readOnly={!editUserForm.admin}
                        value={editUserForm.nome}
                        onChange={handleEditUserInputChange}
                        placeholder="Digite o nome"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="edit-user-ra">{editUserForm.admin === true ? "LOGIN" : "RA"}</label>
                      <input
                        type="text"
                        id="edit-user-ra"
                        name="ra"
                        disabled={!editUserForm.admin}
                        readOnly={!editUserForm.admin}
                        value={editUserForm.ra}
                        onChange={handleEditUserInputChange}
                        placeholder={editUserForm.admin === true ? "Digite o login" : "Digite o RA"}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="edit-user-email">EMAIL</label>
                      <input
                        type="email"
                        id="edit-user-email"
                        name="email"
                        value={editUserForm.email}
                        disabled={!editUserForm.admin}
                        readOnly={!editUserForm.admin}
                        onChange={handleEditUserInputChange}
                        placeholder="usuario@exemplo.com"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="edit-user-telefone">TELEFONE</label>
                      <div className="input-with-icon">
                        <input
                          type="text"
                          id="edit-user-telefone"
                          name="telefone"
                          value={editUserForm.telefone}
                          disabled={!editUserForm.admin}
                          readOnly={!editUserForm.admin}
                          onChange={(e) => {
                            const formatted = formatPhoneInput(e.target.value);
                            setEditUserForm(prev => ({
                              ...prev,
                              telefone: formatted
                            }));
                          }}
                          placeholder="(11) 99999-9999"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-row">
                    {/* {editUserForm.admin === true ?
                    <div className="form-group">
                      <label htmlFor="edit-user-foto">FOTO (URL)</label>
                      <input
                        type="url"
                        id="edit-user-foto"
                        name="foto"
                        maxLength={255}
                        value={editUserForm.foto}
                        disabled={!editUserForm.admin}
                        readOnly={!editUserForm.admin}
                        onChange={handleEditUserInputChange}
                        placeholder="https://exemplo.com/foto.jpg"
                      />
                    </div>
                    : ""} */}

                    {editUserForm.admin === true ?
                      <div className="form-group">
                        <label htmlFor="edit-user-password">NOVA SENHA</label>
                        <input
                          type="password"
                          id="edit-user-password"
                          name="password"
                          value={editUserForm.password}
                          onChange={handleEditUserInputChange}
                          placeholder="Deixe em branco para manter a senha atual"
                        />
                      </div>
                      : ""}

                  </div>
                </div>

                <div className="form-section">
                  <h4>Configurações do Sistema</h4>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="edit-user-active">STATUS</label>
                      <select
                        id="edit-user-active"
                        name="active"
                        value={editUserForm.active}
                        onChange={(e) => {
                          setEditUserForm(prev => ({
                            ...prev,
                            active: e.target.value === 'true'
                          }));
                        }}
                        required
                      >
                        <option value={true}>Ativo</option>
                        <option value={false}>Inativo</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={handleCloseEditUserModal}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="save-btn"
                    disabled={editUserLoading}
                  >
                    {editUserLoading ? 'Salvando...' : 'Salvar Alterações'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Visualização de Usuário (Mobile Only) */}
      {showUserViewModal && viewingUser && (
        <div className="modal-overlay user-view-modal-overlay">
          <div className="modal-content user-view-modal">
            <div className="modal-header">
              <button className="go-back-btn" onClick={handleCloseUserViewModal}>
                <img src="/images/dashboard/svg/arrow_back.svg" alt="" className="go-back-btn-icon" />
              </button>
              <h3 className='modal-title'>DADOS DO USUÁRIO</h3>
              <button className="close-btn" onClick={handleCloseUserViewModal}>✕</button>
            </div>

            <div className="user-view-content">
              <div className="user-profile-wrapper">
                {viewingUser.foto ? (
                  <img src={viewingUser.foto} alt="" className="avatar-img user-view-avatar-img" />
                ) : (
                  <div className="user-view-avatar-placeholder">
                    {viewingUser.nome?.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="user-view-profile-info-container">
                  <div className="user-view-profile-info name">{viewingUser.nome}</div>
                  <div className="user-view-profile-info date">{formatDate(viewingUser.created_at)}</div>
                  <div className="user-view-field status">
                    <label htmlFor='user-view-status'>Status:</label>
                    <div name="user-view-status" className="user-view-profile-info" className={`status-badge ${getStatusColor(viewingUser.active === true ? "Ativo" : "Inativo")}`}>
                      {viewingUser.active === true ? "Ativo" : "Inativo"}
                    </div>
                  </div>
                </div>
              </div>

              <div className="user-view-fields">
                <div className="user-view-field-container">
                  <img src="/images/admin/svg/backpack.svg" alt="" className='user-view-field-icon' />
                  <div className="user-view-field">
                    <label>Login / RA</label>
                    <span>{viewingUser.ra}</span>
                  </div>
                </div>
                <div className="user-view-field-container">
                  <img src="/images/admin/svg/backpack.svg" alt="" className='user-view-field-icon' />
                  <div className="user-view-field">
                    <label>E-mail</label>
                    <span>{viewingUser.email}</span>
                  </div>
                </div>
                <div className="user-view-field-container">
                  <img src="/images/admin/svg/backpack.svg" alt="" className='user-view-field-icon' />
                  <div className="user-view-field">
                    <label>Telefone</label>
                    <span>{formatPhone(viewingUser.telefone) || '—'}</span>
                  </div>
                </div>
                <div className="user-view-field-container">
                  <img src="/images/admin/svg/backpack.svg" alt="" className='user-view-field-icon' />
                  <div className="user-view-field">
                    <label>Tipo</label>
                    <div className={`status-badge ${getStatusColor(viewingUser.admin === true ? "admin" : "no-admin")}`}>
                      {viewingUser.admin === true ? "Admin" : "Aluno"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="save-btn"
                onClick={() => { handleCloseUserViewModal(); handleEditUser(viewingUser); }}
              >
                Editar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
